import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_PLANS } from '@/graphql/subscription-queries'
import { SUBSCRIPTION_PLANS, BILLING_CYCLES } from '@/pages/users/Subscriptions/config'
import {
    SUBSCRIPTION_LICENSE,
    SUBSCRIPTION_SUPPORT,
    SUBSCRIPTION_CREATIVE,
} from '@/pages/users/Subscriptions/constants'
import PlanCard from '@/pages/users/Subscriptions/PlanCard/PlanCard'
import { useSubscriptionPlanSelection } from '@/pages/users/Subscriptions/useSubscriptionPlanSelection'
import AddCardInline from '@/components/payments/AddCardInline'
import { CubeLoader } from '@/components/ui/cube-loader'

const DEFAULT_CYCLE = 'quarterly'
const DEFAULT_TIER = 'start'

const DEFAULT_UI = {
    billingCycles: { [SUBSCRIPTION_LICENSE]: DEFAULT_CYCLE, [SUBSCRIPTION_SUPPORT]: DEFAULT_CYCLE },
    creativeTier: DEFAULT_TIER,
    creativeFrequency: DEFAULT_CYCLE,
    licenseSelected: true,
    supportEnabled: false,
    creativesEnabled: false,
}

const SubscriptionStep = ({
    initialPaymentMethodId,
    initialUi,
    onSubmit,
    submitting = false,
    onFooterStateChange,
}) => {
    const [supportEnabled, setSupportEnabled] = useState(
        () => initialUi?.supportEnabled ?? DEFAULT_UI.supportEnabled
    )
    const [creativesEnabled, setCreativesEnabled] = useState(
        () => initialUi?.creativesEnabled ?? DEFAULT_UI.creativesEnabled
    )
    // The saved payment method can arrive after this step has already mounted (e.g. a cold cache on
    // the draft-completion deep link, where `initialPaymentMethodId` is still null at first render).
    // `chosenPaymentMethodId` is only ever set by the customer's own action — restoring a prior
    // submission's snapshot, or entering a new card — so it is never clobbered by that late arrival.
    // `paymentMethodId` re-derives every render, so a `initialPaymentMethodId` that resolves later is
    // adopted automatically as long as the customer hasn't chosen one of their own in the meantime.
    const [chosenPaymentMethodId, setChosenPaymentMethodId] = useState(
        () => initialUi?.paymentMethodId ?? null
    )
    const paymentMethodId = chosenPaymentMethodId ?? initialPaymentMethodId ?? null
    const [licenseSelected, setLicenseSelected] = useState(
        () => initialUi?.licenseSelected ?? DEFAULT_UI.licenseSelected
    )

    const { data: plansData, loading: plansLoading, error: plansError } = useQuery(GET_PLANS)
    const plans = useMemo(() => plansData?.plans ?? [], [plansData])
    const {
        billingCycles,
        creativeTier,
        creativeFrequency,
        handleCycleChange,
        handleTierChange,
        handleCreativeFrequencyChange,
        selectedPlans,
        amounts,
    } = useSubscriptionPlanSelection({
        plans,
        initialBillingCycles: initialUi?.billingCycles ?? DEFAULT_UI.billingCycles,
        initialCreativeTier: initialUi?.creativeTier ?? DEFAULT_UI.creativeTier,
        initialCreativeFrequency: initialUi?.creativeFrequency ?? DEFAULT_UI.creativeFrequency,
    })
    const licensePlan = selectedPlans[SUBSCRIPTION_LICENSE]
    const supportPlan = selectedPlans[SUBSCRIPTION_SUPPORT]
    const creativesPlan = selectedPlans[SUBSCRIPTION_CREATIVE]

    const missingLicensePlan = !plansLoading && !licensePlan
    const canSubmit =
        !!paymentMethodId &&
        licenseSelected &&
        !!licensePlan &&
        (!supportEnabled || !!supportPlan) &&
        (!creativesEnabled || !!creativesPlan)

    const handleSubscribeToggle = (planId) => {
        if (planId === SUBSCRIPTION_LICENSE) return
        if (planId === SUBSCRIPTION_SUPPORT) setSupportEnabled((v) => !v)
        else if (planId === SUBSCRIPTION_CREATIVE) setCreativesEnabled((v) => !v)
    }

    const handleCardSaved = (user) => {
        if (user?.paymentMethodId) setChosenPaymentMethodId(user.paymentMethodId)
    }

    const handleNext = useCallback(() => {
        if (!licensePlan || !paymentMethodId) return
        const inputs = {
            licenseSubscription: { planId: licensePlan.id, paymentMethodId },
            ...(supportEnabled && supportPlan
                ? { supportSubscription: { planId: supportPlan.id, paymentMethodId } }
                : {}),
            ...(creativesEnabled && creativesPlan
                ? { creativesSubscription: { planId: creativesPlan.id, paymentMethodId } }
                : {}),
        }
        const uiSnapshot = {
            billingCycles,
            creativeTier,
            creativeFrequency,
            licenseSelected,
            supportEnabled,
            creativesEnabled,
            paymentMethodId,
        }
        onSubmit(inputs, uiSnapshot)
    }, [
        billingCycles,
        creativeFrequency,
        creativeTier,
        creativesEnabled,
        creativesPlan,
        licensePlan,
        licenseSelected,
        onSubmit,
        paymentMethodId,
        supportEnabled,
        supportPlan,
    ])

    useEffect(() => {
        if (!onFooterStateChange) return
        onFooterStateChange({ canSubmit, handleSubmit: handleNext })
    }, [canSubmit, handleNext, onFooterStateChange])

    return (
        <div className="tw:relative tw:h-full tw:flex tw:flex-col tw:gap-0">
            <div className="tw:h-full tw:flex tw:flex-col tw:gap-6 tw:p-6 tw:flex-1 tw:min-h-0">
                {!paymentMethodId && (
                    <AddCardInline
                        title="Add a payment method"
                        description="We'll charge this card after you submit the campaign details."
                        onSaved={handleCardSaved}
                    />
                )}

                <div>
                    <p className="tw:text-white tw:text-lg tw:font-semibold">Select subscriptions</p>
                    <p className="tw:text-sm tw:text-[#97AFCD] tw:mt-1">
                        License is required to activate your campaign. Support and Creative are optional
                        and can be added now or later.
                    </p>
                </div>

                {plansLoading ? (
                    <div className="tw:flex tw:min-h-[320px] tw:w-full tw:items-center tw:justify-center">
                        <CubeLoader />
                    </div>
                ) : plansError ? (
                    <p className="tw:text-[#E57373] tw:text-sm">
                        Failed to load plans. Please refresh the page.
                    </p>
                ) : missingLicensePlan ? (
                    <p className="tw:text-[#E57373] tw:text-sm">
                        No License plan is configured for {BILLING_CYCLES[billingCycles[SUBSCRIPTION_LICENSE]].label}.
                        Please pick another billing cycle or ask your administrator to configure a plan.
                    </p>
                ) : (
                    <div className="tw:flex tw:flex-col tw:gap-4 tw:min-[500px]:flex-row tw:min-[500px]:overflow-x-auto tw:min-[1000px]:overflow-x-visible tw:min-[1000px]:justify-center">
                        {SUBSCRIPTION_PLANS.map((plan) => {
                            const isRequired = plan.id === SUBSCRIPTION_LICENSE
                            const isActive =
                                (isRequired && licenseSelected) ||
                                (plan.id === SUBSCRIPTION_SUPPORT && supportEnabled) ||
                                (plan.id === SUBSCRIPTION_CREATIVE && creativesEnabled)

                            return (
                                <PlanCard
                                    key={plan.id}
                                    plan={plan}
                                    cycle={billingCycles[plan.id]}
                                    tier={creativeTier}
                                    creativeFrequency={
                                        plan.id === SUBSCRIPTION_CREATIVE ? creativeFrequency : undefined
                                    }
                                    onCreativeFrequencyChange={
                                        plan.id === SUBSCRIPTION_CREATIVE
                                            ? handleCreativeFrequencyChange
                                            : undefined
                                    }
                                    selectedCreativesQuantity={
                                        plan.id === SUBSCRIPTION_CREATIVE
                                            ? (creativesPlan?.creativesQuantity ?? null)
                                            : undefined
                                    }
                                    isSubscribed={isActive}
                                    onCycleChange={handleCycleChange}
                                    onTierChange={handleTierChange}
                                    onSubscribe={handleSubscribeToggle}
                                    disabled={isRequired && licenseSelected}
                                    subscribedLabel={isRequired ? 'Included' : 'Added'}
                                    idleLabel="Add"
                                    amount={amounts[plan.id]}
                                    disableWhenSubscribed={isRequired}
                                />
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

export default SubscriptionStep

